import { calendarDate } from './financialMath.js';
import { openDB } from 'idb';
import { defaultCategories } from '../data/defaultData.js';
import { markLocalDirty,withWorkspaceWriteLock } from '../lib/expenseAutoSyncState.js';

const DB_NAME = 'ExpenseManagerPro';
const DB_VERSION = 2;

class DatabaseService {
  constructor() {
    this.db = null;
    this.opening = null;
    this.dbName = DB_NAME;
    this.workspaceUserId = null;
    this.restricted = false; // Legacy-only unit tests; the customer gate locks this before sign-in.
  }

  // A BPA user gets a different physical IndexedDB database. The original
  // ExpenseManagerPro database stays untouched for explicit legacy migration.
  useCustomerWorkspace(userId) {
    if (!/^[a-z0-9]{15}$/.test(String(userId || ''))) {
      throw new Error('A verified BPA customer is required for financial storage.');
    }
    const name = DB_NAME + ':bpa:' + userId;
    if (name !== this.dbName) {
      if (this.db) this.db.close();
      this.db = null;
      this.opening = null;
      this.dbName = name;
    }
    this.workspaceUserId = userId;
    this.restricted = false;
  }

  releaseWorkspace() {
    if (this.db) this.db.close();
    this.db = null;
    this.opening = null;
    this.workspaceUserId = null;
    this.dbName = DB_NAME;
    this.restricted = true;
  }

  getWorkspaceUser() { return this.workspaceUserId; }

  async init() {
    if (this.restricted) throw new Error('Sign in to your BPA account before using local financial records.');
    if (this.db) return this.db;
    if (this.opening) return this.opening;
    const activeName = this.dbName;

    const opening = (async () => {
    const opened = await openDB(activeName, DB_VERSION, {
      upgrade(db, oldVersion, newVersion, upgradeTransaction) {
        // Categories store
        if (!db.objectStoreNames.contains('categories')) {
          const categoriesStore = db.createObjectStore('categories', {
            keyPath: 'id',
            autoIncrement: true,
          });
          categoriesStore.createIndex('name', 'name');
          categoriesStore.createIndex('type', 'type');
        }

        // Transactions store
        if (!db.objectStoreNames.contains('transactions')) {
          const transactionsStore = db.createObjectStore('transactions', {
            keyPath: 'id',
            autoIncrement: true,
          });
          transactionsStore.createIndex('date', 'date');
          transactionsStore.createIndex('category', 'category');
          transactionsStore.createIndex('type', 'type');
          transactionsStore.createIndex('person', 'person');
        }

        // Budgets store
        if (!db.objectStoreNames.contains('budgets')) {
          const budgetsStore = db.createObjectStore('budgets', {
            keyPath: 'id',
            autoIncrement: true,
          });
          budgetsStore.createIndex('category', 'category');
        }

        // People store
        if (!db.objectStoreNames.contains('people')) {
          const peopleStore = db.createObjectStore('people', {
            keyPath: 'id',
            autoIncrement: true,
          });
          peopleStore.createIndex('name', 'name');
        }

        // IOUs store
        if (!db.objectStoreNames.contains('ious')) {
          const iousStore = db.createObjectStore('ious', {
            keyPath: 'id',
            autoIncrement: true,
          });
          iousStore.createIndex('personId', 'personId');
          iousStore.createIndex('date', 'date');
        }

        // Recurring Rules store
        if (!db.objectStoreNames.contains('recurringRules')) {
          const recurringStore = db.createObjectStore('recurringRules', {
            keyPath: 'id',
            autoIncrement: true,
          });
          recurringStore.createIndex('name', 'name');
          recurringStore.createIndex('category', 'category');
        }

        // Indexed uniqueness for newly generated occurrences (legacy rows are
        // still checked by date, because they do not carry recurringKey).
        const transactions = upgradeTransaction.objectStore('transactions');
        if (!transactions.indexNames.contains('recurringKey')) {
          transactions.createIndex('recurringKey','recurringKey',{unique:true});
        }

        // Settings store
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings', {
            keyPath: 'key',
          });
        }
      },
    });

    // A user may sign out while a database is opening. Never make that
    // database active in a later customer's session.
    if (this.dbName !== activeName) {
      opened.close();
      throw new Error('Financial workspace changed during initialisation.');
    }
    this.db = opened;
    try { await this.initializeDefaults(); }
    catch (error) {
      opened.close();
      if (this.db === opened) this.db = null;
      throw error;
    }
    return opened;
    })();
    this.opening = opening;
    try { return await opening; }
    finally { if (this.opening === opening) this.opening = null; }
  }

  async initializeDefaults() {
    const categories = await this.getCategories();
    if (categories.length === 0) {
      for (const category of defaultCategories) {
        await this.addCategory(category);
      }
    }
  }

  // Categories
  async getCategories() {
    const db = await this.init();
    return db.getAll('categories');
  }

  async addCategory(category) {
    const db = await this.init();
    // Remove id field if it exists (IndexedDB will auto-generate)
    const { id, ...categoryData } = category;
    const categoryWithTimestamp = {
      ...categoryData,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    return db.add('categories', categoryWithTimestamp);
  }

  async updateCategory(id, updates) {
    const db = await this.init();
    const category = await db.get('categories', id);
    if (category) {
      const updatedCategory = {
        ...category,
        ...updates,
        updatedAt: new Date(),
      };
      await db.put('categories', updatedCategory);
      return updatedCategory;
    }
    return null;
  }

  async deleteCategory(id) {
    const db = await this.init();
    return db.delete('categories', id);
  }

  // Transactions
  async getTransactions() {
    const db = await this.init();
    return db.getAll('transactions');
  }

  async addTransaction(transaction) {
    const db = await this.init();
    console.log('Adding transaction:', transaction);
    
    // Remove id field if it exists (IndexedDB will auto-generate)
    const { id, ...transactionData } = transaction;
    const transactionWithTimestamp = {
      ...transactionData,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    
    console.log('Transaction data to add:', transactionWithTimestamp);
    const result = await db.add('transactions', transactionWithTimestamp);
    console.log('Transaction added with ID:', result);
    return result;
  }

  // Atomic check-and-insert avoids duplicate daily allocations from repeated generation.
  async addRecurringTransactionIfAbsent(transaction) {
    const db = await this.init();
    const tx = db.transaction('transactions', 'readwrite');
    const store = tx.objectStore('transactions');
    const existing = await store.index('date').getAll(transaction.date);
    const duplicate = existing.some(item =>
      item.recurringRuleId === transaction.recurringRuleId &&
      item.date === transaction.date &&
      Boolean(item.isDailyBreakdown) === Boolean(transaction.isDailyBreakdown)
    );
    if (duplicate) {
      await tx.done;
      return null;
    }
    const { id, ...transactionData } = transaction;
    const recurringKey = String(transaction.recurringRuleId) + ':' + transaction.date + ':' +
      (transaction.isDailyBreakdown ? 'daily' : 'period');
    const now = new Date();
    const newId = await store.add({ ...transactionData, recurringKey, createdAt: now, updatedAt: now });
    await tx.done;
    return newId;
  }

  /**
   * Commit a full recurring generation as ONE IndexedDB transaction. This
   * prevents partially written daily breakdowns after a storage failure,
   * and serialises competing writer tabs through the readwrite transaction.
   * Keep the historic rule/date/mode duplicate key for legacy compatibility.
   *
   * No network or Google calls occur here: Sheets will batch a later Sync Now.
   */
  async addRecurringTransactionsBatchIfAbsent(transactions) {
    if (!Array.isArray(transactions) || transactions.length > 5000) {
      throw new Error('A generation can contain at most 5,000 entries. Select fewer occurrences.');
    }
    if (transactions.length === 0) return { saved: [], skipped: 0 };
    for (const item of transactions) {
      if (!item.recurringRuleId || !/^\d{4}-\d{2}-\d{2}$/.test(item.date || '')) {
        throw new Error('A generated transaction has an invalid recurring rule or date.');
      }
      // Google Sheets stores the complete record JSON in one cell. Reject
      // records larger than the safe cell bound before writing ANY entry.
      if (JSON.stringify(item).length > 45000) {
        throw new Error('A generated transaction exceeds the safe Google Sheets cell size.');
      }
    }

    const db = await this.init();
    const tx = db.transaction('transactions', 'readwrite');
    const store = tx.objectStore('transactions');
    const seen = new Set();
    const saved = [];
    let skipped = 0;
    try {
      // Read existing dates under the same exclusive write transaction.
      const dates = [...new Set(transactions.map(item => item.date))];
      for (const date of dates) {
        const existing = await store.index('date').getAll(date);
        for (const item of existing) {
          if (item.recurringRuleId == null) continue;
          seen.add(String(item.recurringRuleId) + ':' + item.date + ':' +
            (item.isDailyBreakdown ? 'daily' : 'period'));
        }
      }
      const now = new Date();
      for (const transaction of transactions) {
        const key = String(transaction.recurringRuleId) + ':' + transaction.date + ':' +
          (transaction.isDailyBreakdown ? 'daily' : 'period');
        if (seen.has(key)) { skipped++; continue; }
        seen.add(key);
        const { id, ...data } = transaction;
        const savedItem = { ...data, recurringKey: key, createdAt: now, updatedAt: now };
        const savedId = await store.add(savedItem);
        saved.push({ ...savedItem, id: savedId });
      }
      await tx.done;
      return { saved, skipped };
    } catch (error) {
      try { tx.abort(); } catch { /* already aborted */ }
      // Drain the rejected transaction promise to avoid unhandled rejection.
      await tx.done.catch(() => {});
      throw error;
    }
  }

  async updateTransaction(id, updates) {
    const db = await this.init();
    console.log('Updating transaction:', id, updates);
    
    const transaction = await db.get('transactions', id);
    if (transaction) {
      const updatedTransaction = {
        ...transaction,
        ...updates,
        cashConfirmed: transaction.isRecurring && !transaction.isDailyBreakdown &&
          updates.status !== undefined ? updates.status === 'posted' : transaction.cashConfirmed,
        updatedAt: new Date(),
      };
      await db.put('transactions', updatedTransaction);
      console.log('Transaction updated:', updatedTransaction);
      return updatedTransaction;
    }
    return null;
  }

  async deleteTransaction(id) {
    const db = await this.init();
    console.log('Deleting transaction:', id);
    return db.delete('transactions', id);
  }

  // Budgets
  async getBudgets() {
    const db = await this.init();
    return db.getAll('budgets');
  }

  async addBudget(budget) {
    const db = await this.init();
    // Remove id field if it exists (IndexedDB will auto-generate)
    const { id, ...budgetData } = budget;
    const budgetWithTimestamp = {
      ...budgetData,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    return db.add('budgets', budgetWithTimestamp);
  }

  async updateBudget(id, updates) {
    const db = await this.init();
    const budget = await db.get('budgets', id);
    if (budget) {
      const updatedBudget = {
        ...budget,
        ...updates,
        updatedAt: new Date(),
      };
      await db.put('budgets', updatedBudget);
      return updatedBudget;
    }
    return null;
  }

  async deleteBudget(id) {
    const db = await this.init();
    return db.delete('budgets', id);
  }

  // People
  async getPeople() {
    const db = await this.init();
    return db.getAll('people');
  }

  async addPerson(person) {
    const db = await this.init();
    // Remove id field if it exists (IndexedDB will auto-generate)
    const { id, ...personData } = person;
    const personWithTimestamp = {
      ...personData,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    return db.add('people', personWithTimestamp);
  }

  async updatePerson(id, updates) {
    const db = await this.init();
    const person = await db.get('people', id);
    if (person) {
      const updatedPerson = {
        ...person,
        ...updates,
        updatedAt: new Date(),
      };
      await db.put('people', updatedPerson);
      return updatedPerson;
    }
    return null;
  }

  async deletePerson(id) {
    const db = await this.init();
    return db.delete('people', id);
  }

  // IOUs
  async getIOUs() {
    const db = await this.init();
    return db.getAll('ious');
  }

  async addIOU(iou) {
    const db = await this.init();
    // Remove id field if it exists (IndexedDB will auto-generate)
    const { id, ...iouData } = iou;
    const iouWithTimestamp = {
      ...iouData,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    return db.add('ious', iouWithTimestamp);
  }

  // Loan transfers may optionally appear in Transactions, but they are
  // explicitly linked so expense/income reports never mistake loans for profit.
  async addIOUAndTransaction(iou, personName) {
    const db = await this.init();
    const tx = db.transaction(['ious', 'transactions'], 'readwrite');
    const now = new Date();
    const { id, createTransaction, ...raw } = iou;
    const iouData = { ...raw, createdAt: now, updatedAt: now };
    try {
      const iouId = await tx.objectStore('ious').add(iouData);
      const outgoing = ['you_lent', 'lent', 'repayment_made'].includes(iou.type);
      const transactionData = {
        type: outgoing ? 'expense' : 'income',
        amount: Number(iou.amount), date: iou.date,
        category: 'IOU transfer', person: personName,
        payee: personName, notes: iou.notes || 'Linked IOU transfer',
        status: iou.date <= calendarDate(new Date()) ? 'posted' : 'pending',
        linkedIOUId: iouId, createdAt: now, updatedAt: now
      };
      const transactionId = await tx.objectStore('transactions').add(transactionData);
      const savedIOU = { ...iouData, id: iouId, linkedTransactionId: transactionId };
      await tx.objectStore('ious').put(savedIOU);
      await tx.done;
      return { iou: savedIOU, transaction: { ...transactionData, id: transactionId } };
    } catch (error) {
      try { tx.abort(); } catch { /* already aborted */ }
      throw error;
    }
  }

  async updateIOUAndLinkedTransaction(id, updates) {
    const db = await this.init();
    const tx = db.transaction(['ious','transactions'],'readwrite');
    try {
      const existing = await tx.objectStore('ious').get(id);
      if (!existing) throw new Error('IOU not found');
      const saved = { ...existing, ...updates, id, updatedAt: new Date() };
      await tx.objectStore('ious').put(saved);
      let linked = null;
      if (existing.linkedTransactionId != null) {
        const stored = await tx.objectStore('transactions').get(existing.linkedTransactionId);
        if (stored) {
          const outgoing = ['you_lent','lent','repayment_made'].includes(saved.type);
          linked = { ...stored, type: outgoing ? 'expense' : 'income',
            amount: Number(saved.amount), date: saved.date, notes: saved.notes || stored.notes,
            updatedAt: new Date() };
          await tx.objectStore('transactions').put(linked);
        }
      }
      await tx.done;
      return { iou: saved, transaction: linked };
    } catch (error) {
      try { tx.abort(); } catch { /* already aborted */ }
      throw error;
    }
  }

  async deleteIOUAndLinkedTransaction(id) {
    const db = await this.init();
    const tx = db.transaction(['ious','transactions'],'readwrite');
    try {
      const iou = await tx.objectStore('ious').get(id);
      if (iou?.linkedTransactionId != null) {
        await tx.objectStore('transactions').delete(iou.linkedTransactionId);
      }
      await tx.objectStore('ious').delete(id);
      await tx.done;
      return iou?.linkedTransactionId ?? null;
    } catch (error) {
      try { tx.abort(); } catch { /* already aborted */ }
      throw error;
    }
  }

  async updateIOU(id, updates) {
    const db = await this.init();
    const iou = await db.get('ious', id);
    if (iou) {
      const updatedIOU = {
        ...iou,
        ...updates,
        updatedAt: new Date(),
      };
      await db.put('ious', updatedIOU);
      return updatedIOU;
    }
    return null;
  }

  async deleteIOU(id) {
    const db = await this.init();
    return db.delete('ious', id);
  }

  // Recurring Rules
  async getRecurringRules() {
    const db = await this.init();
    return db.getAll('recurringRules');
  }

  async addRecurringRule(rule) {
    const db = await this.init();
    // Remove id field if it exists (IndexedDB will auto-generate)
    const { id, ...ruleData } = rule;
    const ruleWithTimestamp = {
      ...ruleData,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    return db.add('recurringRules', ruleWithTimestamp);
  }

  async updateRecurringRule(id, updates) {
    const db = await this.init();
    const rule = await db.get('recurringRules', id);
    if (rule) {
      const updatedRule = {
        ...rule,
        ...updates,
        updatedAt: new Date(),
      };
      await db.put('recurringRules', updatedRule);
      return updatedRule;
    }
    return null;
  }

  async deleteRecurringRule(id) {
    const db = await this.init();
    return db.delete('recurringRules', id);
  }

  // Settings
  async getSetting(key) {
    const db = await this.init();
    const setting = await db.get('settings', key);
    return setting?.value;
  }

  async setSetting(key, value) {
    const db = await this.init();
    return db.put('settings', { key, value });
  }

  // Data management
  async exportData() {
    const [
      categories,
      transactions,
      budgets,
      people,
      ious,
      recurringRules,
      settings,
    ] = await Promise.all([
      this.getCategories(),
      this.getTransactions(),
      this.getBudgets(),
      this.getPeople(),
      this.getIOUs(),
      this.getRecurringRules(),
      this.getAllSettings(),
    ]);

    return {
      version: '2.0.0',
      exportDate: new Date().toISOString(),
      data: {
        categories,
        transactions,
        budgets,
        people,
        ious,
        recurringRules,
        settings,
      },
    };
  }

  async importData(backupData) {
    const db = await this.init();
    const data = backupData?.data;
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid backup format');
    }

    const stores = ['categories', 'transactions', 'budgets', 'people', 'ious', 'recurringRules', 'settings'];
    // Validate all datasets BEFORE touching existing records.
    for (const store of stores) {
      if (!Array.isArray(data[store])) {
        throw new Error(`Incomplete backup: ${store} must be included as an array`);
      }
      const ids = new Set();
      for (const item of data[store]) {
        if (!item || typeof item !== 'object' || Array.isArray(item)) {
          throw new Error(`Invalid item in ${store}`);
        }
        const value = store === 'settings' ? item.key : item.id;
        if (value == null || value === '') throw new Error(`Missing key in ${store}`);
        if (ids.has(String(value))) throw new Error(`Duplicate key in ${store}`);
        ids.add(String(value));
      }
    }
    const personIds = new Set(data.people.map(p => String(p.id)));
    for (const entry of data.ious) {
      if (!personIds.has(String(entry.personId))) {
        throw new Error('Backup IOU references a missing person');
      }
    }

    // A single transaction prevents losing the previous database if restoring fails.
    // Preserve original IDs so links between people, IOUs, categories and rules remain valid.
    const tx = db.transaction(stores, 'readwrite');
    try {
      for (const store of stores) {
        await tx.objectStore(store).clear();
        for (const item of data[store] || []) {
          await tx.objectStore(store).put(item);
        }
      }
      await tx.done;
    } catch (error) {
      try { tx.abort(); } catch { /* transaction already rolled back */ }
      throw error;
    }
  }

  async clearAllData() {
    const db = await this.init();
    const tx = db.transaction([
      'categories',
      'transactions',
      'budgets',
      'people',
      'ious',
      'recurringRules',
      'settings',
    ], 'readwrite');

    await Promise.all([
      tx.objectStore('categories').clear(),
      tx.objectStore('transactions').clear(),
      tx.objectStore('budgets').clear(),
      tx.objectStore('people').clear(),
      tx.objectStore('ious').clear(),
      tx.objectStore('recurringRules').clear(),
      tx.objectStore('settings').clear(),
    ]);

    await tx.done;
  }

  async getAllSettings() {
    const db = await this.init();
    return db.getAll('settings');
  }

  // Explicit, non-destructive migration of the pre-login browser database.
  // Never guess which customer owns legacy records or automatically upload them.
  async importLegacyIntoWorkspace({confirmed = false} = {}) {
    const userId = this.workspaceUserId;
    if (!userId || !confirmed) throw new Error('Sign in and confirm ownership before importing old local data.');
    const key = 'bpa-expense-local-owner-v1';
    const priorOwner = localStorage.getItem(key);
    if (priorOwner && priorOwner !== userId) {
      throw new Error('The original local database belongs to another BPA customer. Nothing was imported.');
    }
    const target = await this.init();
    const stores = ['categories','transactions','budgets','people','ious','recurringRules','settings'];
    for (const store of stores.filter(name => name !== 'categories')) {
      if ((await target.getAll(store)).length) {
        throw new Error('The current customer workspace already contains data. Export a backup before importing; nothing was changed.');
      }
    }
    const legacy = await openDB(DB_NAME);
    let data;
    try {
      if (stores.some(name => !legacy.objectStoreNames.contains(name))) {
        throw new Error('The original Expense Manager database was not found. Nothing was imported.');
      }
      const rows = await Promise.all(stores.map(name => legacy.getAll(name)));
      data = Object.fromEntries(stores.map((name, index) => [name, rows[index]]));
    } finally { legacy.close(); }
    if (stores.every(name => data[name].length === 0)) throw new Error('There is no original data to import.');
    await this.importData({version:'2.0.0',exportDate:new Date().toISOString(),data});
    localStorage.setItem(key, userId);
    return Object.fromEntries(stores.map(name => [name,data[name].length]));
  }

  // Utility methods
  async getStats() {
    const [
      categories,
      transactions,
      budgets,
      people,
      ious,
      recurringRules,
    ] = await Promise.all([
      this.getCategories(),
      this.getTransactions(),
      this.getBudgets(),
      this.getPeople(),
      this.getIOUs(),
      this.getRecurringRules(),
    ]);

    return {
      categories: categories.length,
      transactions: transactions.length,
      budgets: budgets.length,
      people: people.length,
      ious: ious.length,
      recurringRules: recurringRules.length,
    };
  }
}

export const dbService = new DatabaseService();

// Emit AFTER an IndexedDB mutation has fully committed, not before. This
// covers finance updates from every screen, including atomic IOU and recurring
// transactions, without adding Google calls inside IndexedDB transactions.
// Debouncing and remote conflict checks are handled by the sync controller.
const SYNC_WRITES=[
 'addCategory','updateCategory','deleteCategory',
 'addTransaction','updateTransaction','deleteTransaction',
 'addRecurringTransactionIfAbsent','addRecurringTransactionsBatchIfAbsent',
 'addBudget','updateBudget','deleteBudget',
 'addPerson','updatePerson','deletePerson',
 'addIOU','addIOUAndTransaction','updateIOU','updateIOUAndLinkedTransaction',
 'deleteIOU','deleteIOUAndLinkedTransaction',
 'addRecurringRule','updateRecurringRule','deleteRecurringRule',
 'setSetting','clearAllData'
];
for(const name of SYNC_WRITES){
 const original=dbService[name].bind(dbService);
 dbService[name]=async (...args)=>{
   const owner=dbService.getWorkspaceUser();
   // Use the same origin-wide Web Lock as automatic cloud refresh. A write
   // queued behind a restore must never execute in a different customer's DB.
   return withWorkspaceWriteLock(owner,async()=>{
     if(owner && dbService.getWorkspaceUser()!==owner)
       throw new Error('BPA workspace changed before the financial edit could be saved.');
     const result=await original(...args);
     const changed=result!==null && (name!=='addRecurringTransactionsBatchIfAbsent' ||
       (result?.saved?.length||0)>0);
     if(changed && owner && dbService.getWorkspaceUser()===owner)markLocalDirty(owner);
     return result;
   });
 };
}